import { useMemo } from 'react';

import { IBaseItem, ISanityItem, TProductHierarchyKey } from '@rbi-ctg/menu';
import {
  IFlavorFlowItemInput,
  ServiceMode,
  useListFlavorFlowItemsQuery,
} from 'generated/rbi-graphql';
import { allItemsQuery } from 'remote/queries/all-items';
import { useSanityQuery } from 'state/graphql';
import { useFlag } from 'state/launchdarkly';
import { useMenuContext } from 'state/menu';
import { useOrderContext } from 'state/order';
import { useStoreContext } from 'state/store';
import { LaunchDarklyFlag } from 'utils/launchdarkly';

import { IUseUpsell } from './types';
import { existsInEntries } from './utils';

const getRandomValue = (): number => {
  const buf = new Uint8Array(1);
  window.crypto.getRandomValues(buf);
  return (buf[0] % 3) - 1;
};

const filterByProductHierarchy = (
  items: IBaseItem[],
  levelFiltered: TProductHierarchyKey
): IBaseItem[] => {
  const itemsFiltered: IBaseItem[] = [];

  for (const item of items) {
    if (
      itemsFiltered.findIndex(
        i => i.productHierarchy?.[levelFiltered] === item.productHierarchy?.[levelFiltered]
      ) === -1
    ) {
      itemsFiltered.push(item);
    }
  }

  return itemsFiltered;
};

export const useFlavorFlowUpsell = () => {
  const { serviceMode } = useOrderContext();
  const { filterForAvailability } = useMenuContext();
  const { store } = useStoreContext();
  const { cartEntries } = useOrderContext();

  const hideItemsInCart = useFlag(LaunchDarklyFlag.FLAVOR_FLOW_HIDE_ITEMS_IN_CART);
  const numberOfItemsDisplayed = useFlag(LaunchDarklyFlag.FLAVOR_FLOW_NUMBER_ITEMS_DISPLAYED);
  const levelFiltered = useFlag(LaunchDarklyFlag.FLAVOR_FLOW_LEVEL_FILTERED);

  const storeId: string = store.number ?? '';

  const productDetails = cartEntries.map((cartEntry: { _id: any; type: any; quantity: any }) => ({
    id: cartEntry._id,
    type: cartEntry.type,
    quantity: cartEntry.quantity,
  })) as IFlavorFlowItemInput[];

  const { loading: upsellLoading, data: upsellData } = useListFlavorFlowItemsQuery({
    variables: {
      productDetails,
      serviceMode: ServiceMode[serviceMode as ServiceMode],
      storeId,
    },
    skip: !storeId,
  });

  const { loading: sanityLoading, data: sanityData } = useSanityQuery(allItemsQuery, {
    variables: {
      where: {
        _id: { in: upsellData?.listFlavorFlowItems?.results?.map(item => item?.id) || [] },
      },
    },
    skip: !upsellData,
  });

  const items: IBaseItem[] = useMemo(() => {
    const results = (upsellData?.listFlavorFlowItems?.results || [])
      .map(upsellItem => sanityData?.allItem.find((item: IBaseItem) => item._id === upsellItem?.id))
      .filter((item: IBaseItem) => {
        // Check if doesn't exists in the cart
        return item && hideItemsInCart ? !existsInEntries(item, cartEntries) : true;
      })
      .filter((item: IBaseItem) => {
        return item && !!filterForAvailability(item as ISanityItem);
      });

    const filteredList = filterByProductHierarchy(results, levelFiltered as TProductHierarchyKey);

    return filteredList.slice(0, numberOfItemsDisplayed).sort(() => getRandomValue());
  }, [
    cartEntries,
    upsellData,
    sanityData,
    filterForAvailability,
    hideItemsInCart,
    numberOfItemsDisplayed,
    levelFiltered,
  ]);

  return {
    items,
    loading: upsellLoading || sanityLoading,
    recommender: upsellData?.listFlavorFlowItems?.recommender,
    recommendationToken: upsellData?.listFlavorFlowItems?.recommendationToken || undefined,
    upsellData: upsellData?.listFlavorFlowItems?.results || undefined,
  } as IUseUpsell;
};
